import { FC, useState, useEffect } from 'react';
import {
  Button,
  Modal,
  Typography,
  Table,
  Checkbox,
  Badge,
  Input,
  Form,
  Toast,
  Progress,
} from '@crisellevaldez/speculo-ui';
import { Download, Upload, Tag, X, FileText, RotateCw, AlertCircle } from 'lucide-react';
import { useUniqueContactTags } from '../../../hooks/contacts/useUniqueContactTags';
import { SearchableSelect } from '../../common/SearchableSelect';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useFileParser, ParsedContact } from '../../../hooks/bulk-upload/useFileParser';
import { bulkUploadContacts, pollUploadStatus } from '../../../api/contacts';
import { UploadStillProcessingError } from '../../../api/uploadStatusPolling';
import { Organization } from '../../../types/organization.types';
import { UploadResultModal, UploadResponse } from './UploadResultModal';
import { useQueryClient } from '@tanstack/react-query';

// Define the schema for bulk upload form
const bulkUploadSchema = z
  .object({
    optInAll: z.boolean().default(false),
    selectedTag: z.string().trim(),
    newTag: z.string().trim().optional(),
  })
  .refine(
    (data) => {
      const isValid = data.selectedTag !== '' || (data.newTag !== undefined && data.newTag !== '');
      return isValid;
    },
    {
      message: 'Either select an existing tag or create a new one',
      path: ['selectedTag'],
    }
  );

type BulkUploadFormData = z.infer<typeof bulkUploadSchema>;

interface BulkUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg?: Organization;
  onSuccess?: () => void;
}

export const BulkUploadModal: FC<BulkUploadModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  // Get the query client for invalidating queries
  const queryClient = useQueryClient();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [customTags, setCustomTags] = useState<string[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [stillProcessingNotice, setStillProcessingNotice] = useState<string | null>(null);
  const [uploadResponse, setUploadResponse] = useState<UploadResponse | null>(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [uploadPhase, setUploadPhase] = useState<'idle' | 'upload' | 'processing' | 'complete'>(
    'idle'
  );

  // Use the file parser hook
  const { parseFile, isLoading: isParsingFile, error: parseError } = useFileParser();

  // Fetch existing tags
  const { data: existingTags = [], isLoading: isTagsLoading, hasMore: tagsHasMore, loadMore: tagsLoadMore, setSearch: tagsSetSearch } = useUniqueContactTags(isOpen);

  // Combine existing tags with custom tags
  const allTags = [...existingTags, ...customTags.filter((tag) => !existingTags.includes(tag))];

  // Initialize form with React Hook Form
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<BulkUploadFormData>({
    resolver: zodResolver(bulkUploadSchema),
    defaultValues: {
      optInAll: false,
      selectedTag: '',
      newTag: '',
    },
  });

  // Watch form values
  const selectedTag = watch('selectedTag');
  const newTag = watch('newTag');

  // Reset state when modal is closed
  useEffect(() => {
    if (!isOpen) {
      setSelectedFile(null);
      setParsedContacts([]);
      setCurrentPage(1);
      reset();
      setCustomTags([]);
    }
  }, [isOpen, reset]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;

    if (file) {
      // Check file extension
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const allowedExtensions = ['csv', 'xlsx', 'xls'];

      if (fileExtension && allowedExtensions.includes(fileExtension)) {
        setSelectedFile(file);
        setFileError(null);

        try {
          // Parse the file using our hook
          const contacts = await parseFile(file);
          setParsedContacts(contacts);
        } catch (error) {
          if (error instanceof Error) {
            setFileError(error.message);
          } else {
            setFileError('Failed to parse file');
          }
          setSelectedFile(null);
          // Reset the file input
          event.target.value = '';
        }
      } else {
        setSelectedFile(null);
        setFileError('Invalid file type. Please select a .csv, .xlsx, or .xls file.');
        // Reset the file input
        event.target.value = '';
      }
    }
  };

  const handleDownloadTemplate = () => {
    // Use the existing template file from the public directory
    const templateUrl = '/template.csv';

    // Create a download link
    const link = document.createElement('a');
    link.href = templateUrl;
    link.setAttribute('download', 'contacts_template.csv');

    // Append the link to the body, click it, and remove it
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handle form submission
  const onSubmit = async (data: BulkUploadFormData) => {
    if (!selectedFile || !selectedOrg?.id) {
      return;
    }

    try {
      setIsUploading(true);
      setUploadError(null);
      setStillProcessingNotice(null);
      setUploadProgress(0);
      setUploadPhase('upload');

      // Get the tag to use (either selected existing tag or new tag)
      let tagToUse = data.selectedTag || data.newTag || '';

      // If we have a new tag that hasn't been processed yet (user didn't press Enter)
      // Make sure it's used for the upload
      if (!data.selectedTag && data.newTag) {
        tagToUse = data.newTag;
      }

      // Upload the file
      const uploadResult = await bulkUploadContacts(
        selectedFile,
        selectedOrg.id,
        data.optInAll,
        tagToUse,
        (progress, phase) => {
          setUploadProgress(progress);
          setUploadPhase(phase);
        }
      );

      let response = uploadResult;

      // If backend returned a request_id, poll for completion
      if (uploadResult.request_id && uploadResult.status !== 'completed') {
        response = await pollUploadStatus(
          uploadResult.request_id,
          selectedOrg.id,
          (status) => {
            if (status.progress != null) {
              setUploadProgress(status.progress);
            }
          }
        );

        if (response.status === 'failed') {
          throw new Error(response.message || 'Upload processing failed');
        }
      }

      // Set the upload response
      setUploadResponse(response as UploadResponse);

      // Set phase to complete
      setUploadPhase('complete');

      // Show the result modal
      setShowResultModal(true);

      setUploadSuccess(true);

      // If a new tag was used, invalidate the emailContactTags query to refresh the tag list
      if (data.newTag && !existingTags.includes(data.newTag)) {
        queryClient.invalidateQueries({ queryKey: ['emailContactTags', selectedOrg?.id] });
      }

      // Call onSuccess callback if provided
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      if (error instanceof UploadStillProcessingError) {
        setStillProcessingNotice(error.message);
        return;
      }
      console.error('Error uploading contacts:', error);
      setUploadError(
        error instanceof Error ? error.message : 'An error occurred while uploading contacts'
      );
    } finally {
      setIsUploading(false);
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(parsedContacts.length / pageSize);
  const paginatedContacts = parsedContacts.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Table columns - showing all fields from the template
  const columns = [
    {
      key: 'crmID',
      header: 'CRM ID',
      width: '100px',
      cell: (row: any) => row.crmID || '-',
    },
    {
      key: 'firstName',
      header: 'First Name',
      width: '120px',
      cell: (row: any) => row.firstName || '-',
    },
    {
      key: 'lastName',
      header: 'Last Name',
      width: '120px',
      cell: (row: any) => row.lastName || '-',
    },
    {
      key: 'phoneNumber',
      header: 'Phone',
      width: '120px',
      cell: (row: any) => row.phoneNumber || '-',
    },
    {
      key: 'phoneStatus',
      header: 'Phone Status',
      width: '120px',
      cell: (row: any) => row.phoneStatus || '-',
    },
    {
      key: 'email',
      header: 'Email',
      width: '180px',
      cell: (row: any) => row.email || '-',
    },
    {
      key: 'source',
      header: 'Source',
      width: '120px',
      cell: (row: any) => row.source || '-',
    },
    {
      key: 'tags',
      header: 'Tags',
      width: '120px',
      cell: (row: any) => row.tags || '-',
    },
    {
      key: 'street',
      header: 'Street',
      width: '180px',
      cell: (row: any) => row.street || '-',
    },
    {
      key: 'city',
      header: 'City',
      width: '120px',
      cell: (row: any) => row.city || '-',
    },
    {
      key: 'state',
      header: 'State',
      width: '80px',
      cell: (row: any) => row.state || '-',
    },
    {
      key: 'zip',
      header: 'ZIP',
      width: '80px',
      cell: (row: any) => row.zip || '-',
    },
    {
      key: 'birthdate',
      header: 'Birthdate',
      width: '100px',
      cell: (row: any) => row.birthdate || '-',
    },
  ];

  return (
    <>
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <Modal.Header>
          <Typography variant="h3" className="font-bold">
            Bulk Upload Contacts
          </Typography>
        </Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            {!selectedFile ? (
              <div className="space-y-4">
                <Typography variant="p" className="text-gray-600 dark:text-gray-400">
                  Upload multiple contacts at once using a CSV file. Download our template to get
                  started.
                </Typography>

                <div className="flex justify-content gap-4">
                  <Button
                    variant="outline"
                    className="mt-2 w-[300px] bg-black dark:bg-white/10 text-white hover:bg-gray-800 dark:hover:bg-white/20 h-10 py-2"
                    onClick={handleDownloadTemplate}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>

                  <div className="space-y-2 w-full">
                    <input
                      type="file"
                      onChange={handleFileSelect}
                      className="hidden"
                      id="bulk-file-upload"
                      accept=".csv,.xlsx,.xls"
                    />
                    <label
                      htmlFor="bulk-file-upload"
                      className="cursor-pointer w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-white/15 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-white/5 hover:bg-gray-50 dark:hover:bg-white/10 focus:outline-none"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      <span>Select file to upload</span>
                    </label>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Allowed file types: .csv, .xlsx, .xls
                    </div>

                    {fileError && (
                      <div className="flex items-center text-red-500 text-sm mt-1">
                        <AlertCircle className="w-4 h-4 mr-1" />
                        <span>{fileError}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="border border-gray-300 dark:border-white/15 rounded-lg p-4 bg-gray-100 dark:bg-white/5">
                  <div className="flex flex-col md:flex-row items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <FileText className="text-gray-500 dark:text-gray-400" />
                      <Typography variant="h6" className="font-medium">
                        File: {selectedFile.name}
                      </Typography>
                    </div>
                    <Button
                      className="bg-white dark:bg-transparent"
                      variant="outline"
                      size="sm"
                      onClick={() => setSelectedFile(null)}
                    >
                      <RotateCw className="w-4 h-4 mr-1" />
                      Change File
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  <form onSubmit={handleSubmit(onSubmit)}>
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-3 mb-2">
                          <Typography variant="p" className="text-sm font-medium">
                            Assign a Tag:
                          </Typography>

                          {(selectedTag || newTag) && (
                            <Badge
                              variant="secondary"
                              className="flex items-center bg-[#ffe5bc] text-[#88612a] dark:bg-amber-900/30 dark:text-amber-300 gap-1 pr-2"
                            >
                              <Tag className="w-3 h-3" />
                              {selectedTag || newTag}
                            </Badge>
                          )}
                        </div>

                        <div className="flex items-center gap-3">
                          <div className="flex-1">
                            <Controller
                              name="selectedTag"
                              control={control}
                              render={({ field }) => (
                                <SearchableSelect
                                  placeholder="Select existing tag"
                                  options={allTags.map((tag) => ({ label: tag, value: tag }))}
                                  value={field.value || ''}
                                  onChange={(value) => {
                                    if (value) {
                                      field.onChange(value);
                                      setValue('newTag', '');
                                    }
                                  }}
                                  isLoading={isTagsLoading}
                                  hasMore={tagsHasMore}
                                  onLoadMore={tagsLoadMore}
                                  onSearch={tagsSetSearch}
                                  searchPlaceholder="Search tags..."
                                />
                              )}
                            />
                          </div>

                          <div className="flex items-center justify-center">
                            <Typography variant="p" className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                              or
                            </Typography>
                          </div>

                          <div className="flex-1">
                            <Controller
                              name="newTag"
                              control={control}
                              render={({ field }) => (
                                <Input
                                  placeholder="Type a new tag"
                                  {...field}
                                  onChange={(e) => {
                                    // Allow spaces in tags
                                    field.onChange(e.target.value);

                                    // Clear the selected tag when typing a new tag
                                    if (e.target.value) {
                                      setValue('selectedTag', '');
                                    }
                                  }}
                                />
                              )}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </form>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <Controller
                        name="optInAll"
                        control={control}
                        render={({ field }) => (
                          <div className="flex items-center">
                            <Checkbox
                              id="optInAll"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                            />
                            <label htmlFor="optInAll" className="ml-2 text-sm cursor-pointer">
                              <Typography variant="p" className="font-medium">
                                Set all contacts as opted-in
                              </Typography>
                            </label>
                          </div>
                        )}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Typography variant="p" className="text-xs text-gray-500 dark:text-gray-400">
                        Preview only. Some information from the file will not be displayed.
                      </Typography>
                    </div>

                    <div className="border dark:border-white/15 rounded-lg overflow-hidden">
                      <Table
                        columns={columns}
                        data={paginatedContacts as any[]}
                        keyExtractor={(item: any) =>
                          `contact-${(item as any).firstName}-${(item as any).lastName}`
                        }
                        emptyStateText="No contacts found in the file"
                      />

                      {parsedContacts.length > 0 && (
                        <div className="flex justify-between items-center p-4 border-t dark:border-white/15">
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            Showing{' '}
                            {Math.min((currentPage - 1) * pageSize + 1, parsedContacts.length)} to{' '}
                            {Math.min(currentPage * pageSize, parsedContacts.length)} of{' '}
                            {parsedContacts.length} contacts
                          </div>

                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                              disabled={currentPage === 1}
                            >
                              Previous
                            </Button>

                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                              }
                              disabled={currentPage === totalPages}
                            >
                              Next
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          {/* Progress bar for upload */}
          {isUploading && (
            <div className="mb-4 space-y-2 w-full">
              <div className="flex justify-between items-center">
                <Typography variant="p" className="text-sm font-medium">
                  {uploadPhase === 'upload'
                    ? `Uploading file: ${uploadProgress}%`
                    : `Processing contacts: ${uploadProgress}%`}
                </Typography>
              </div>

              <Progress value={uploadProgress} className="h-2" />

              <Typography variant="p" className="text-xs text-gray-500 dark:text-gray-400">
                {uploadPhase === 'upload'
                  ? 'Uploading your file to the server...'
                  : 'Your contacts are being processed. This may take a few minutes for large files...'}
              </Typography>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} className="h-8" disabled={isUploading}>
              Cancel
            </Button>
            <Button
              onClick={() => {
                handleSubmit(onSubmit)();
              }}
              className="h-8"
              disabled={
                !selectedFile || (!selectedTag && !newTag) || isUploading || !selectedOrg?.id
              }
              isLoading={isUploading}
            >
              Upload Contacts
            </Button>
          </div>

          {uploadError && (
            <div className="mt-2 text-red-500 text-sm flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              <span>{uploadError}</span>
            </div>
          )}

          {stillProcessingNotice && (
            <div className="mt-2 text-amber-500 dark:text-amber-400 text-sm flex items-center">
              <AlertCircle className="w-4 h-4 mr-1" />
              <span>{stillProcessingNotice}</span>
            </div>
          )}

          {!selectedOrg?.id && (
            <div className="mt-2 text-amber-500 dark:text-amber-400 text-sm">
              Organization ID not available. Please try again later.
            </div>
          )}
        </Modal.Footer>
      </Modal>

      {/* Upload Result Modal */}
      <UploadResultModal
        isOpen={showResultModal}
        onClose={() => setShowResultModal(false)}
        uploadResponse={uploadResponse}
        timezone={selectedOrg?.timezone}
        fileName={selectedFile?.name}
      />
    </>
  );
};
